import appManager from '@app/appManager';
import { SET_CUSTOMER_EVENT } from '@servicetitan/contactcenter-agent-inbox-mfe-loader';
import InboxModule from '@modules/InboxModule';
import { inboxActions, inboxState } from '@modules/InboxModule/state';
import { selectReturnCard } from '@modules/InboxModule/lib/selectReturnCard';
import TasksModule from '@modules/TasksModule';
import type { DeferredSelection } from '@modules/TasksModule/types';
import {
  HOME_VIEW_KEY,
  emitToView,
  getCloseState,
  isCloseAsking,
  isGuarded,
  isLeaveAsking,
  rejoinClosePrompt,
  requestClose,
  setCloseState,
} from '../CustomerMfeAdapter/taskCloseGuard';
import type { BookingCompletedPayload } from '../CustomerMfeAdapter/hostOptions';
import {
  captureReturnViewKey,
  selectForCloseIfNeeded,
} from '../TaskVoiceCard/TaskVoiceActions/closeEndedCall';

// same key as InboxModule, so failures share its diagnostics source
const logger = appManager.logger('Inbox');

/**
 * Removes a manual booking card; if it was selected, selection returns to the
 * card the X was clicked from. Selection moves first, so the removed card is
 * never recorded as the one to go back to.
 */
const removeManualBooking = (taskId: string, returnViewKey?: string) => {
  if (inboxState.selectedTaskId === taskId) {
    selectReturnCard(returnViewKey);
  }
  TasksModule.deleteTask(taskId);
  inboxActions.clearPreviousSelectedTaskId(taskId);
};

/**
 * The X on a manual booking card. With no booking started it closes the card
 * at once. With one started it asks "Dismiss this booking?": through the
 * card's own customer-mfe prompt when the CSR is looking at the card, else
 * through the host's prompt. Dismiss booking closes the card, which unmounts
 * its view and discards the booking; Keep booking leaves it open. Same states
 * and guards as Close on an ended call card (`closeEndedCall`), without its
 * classification check: a manual booking has no call.
 */
export async function closeManualBooking(taskId: string): Promise<void> {
  const task = TasksModule.getTask(taskId);
  if (!task) {
    return;
  }
  const viewKey = task.viewKey;
  const returnViewKey = captureReturnViewKey(task.id);

  if (!viewKey || !isGuarded(viewKey)) {
    removeManualBooking(task.id, returnViewKey);
    return;
  }

  const state = getCloseState(viewKey);
  if (state === 'handshake') {
    rejoinClosePrompt(viewKey);
    return;
  }
  // the prompts are modal, so another card's is reachable only programmatically
  if (state !== 'idle' || isCloseAsking() || isLeaveAsking()) {
    return;
  }

  const isVisible = inboxState.selectedTaskId === task.id;
  selectForCloseIfNeeded(task.id);
  setCloseState(viewKey, isVisible ? 'handshake' : 'prompt');

  let deferred: DeferredSelection | undefined;
  try {
    const result = await requestClose(viewKey, { askMfe: isVisible });
    deferred = result.deferredSelection;
    if (result.decision === 'proceed') {
      const active = TasksModule.getTaskByViewKey(viewKey);
      if (active) {
        removeManualBooking(active.id, returnViewKey);
      }
    }
  } catch (error) {
    logger.error('Dismissing a manual booking failed', error);
  } finally {
    try {
      InboxModule.applyDeferredSelection(deferred);
    } catch (error) {
      logger.error('Dismissing a manual booking failed', error);
    }
    setCloseState(viewKey, 'idle');
  }
}

/**
 * A job booked on a manual booking card has settled: the card closes on its
 * own. If the CSR is still on it, they land on that customer's context panel
 * in the Inbox home view; customer-mfe has already shown the booked-job toast.
 * A booking already open on the home view stays as it is instead. If they
 * have moved to another card, the card just goes.
 */
export function completeManualBooking(
  taskId: string,
  booked: BookingCompletedPayload,
): void {
  const task = TasksModule.getTask(taskId);
  if (!task) {
    return;
  }
  if (inboxState.selectedTaskId === task.id) {
    const orgNodeId: string | undefined =
      booked.orgNodeId ?? task.attributes.customerOrgNodeId;
    // a re-drive would drop a home-view booking with no prompt
    if (orgNodeId && !isGuarded(HOME_VIEW_KEY)) {
      emitToView(HOME_VIEW_KEY, SET_CUSTOMER_EVENT, {
        customerId: booked.customerId,
        orgNodeId,
      });
    }
    // go home first, so the removed card is not recorded as the previous one
    InboxModule.selectTaskByUser(undefined);
  }
  TasksModule.deleteTask(task.id);
  inboxActions.clearPreviousSelectedTaskId(task.id);
}
